import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { JSONContent } from '@tiptap/react';
import { Container, Section, Button, showAlert, showConfirm } from '@platform/ui';
import { toUserMessage } from '@platform/api-client';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import { PostViewer, formatBoardDateTime } from '@/features/board';
import { PostEditor } from '@/features/board';
import { useAuth } from '@/features/auth';
import { hasMinRole, USER_ROLE } from '@/shared/constants/auth/roles.constant.ts';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useInquiryDetail } from '../api/use-inquiry.query.ts';
import {
  useAnswerInquiry,
  useDeleteInquiry,
  useMarkInquiryRead,
} from '../api/use-inquiry.mutation.ts';
import {
  INQUIRY_CATEGORY_LABEL,
  INQUIRY_STATUS_CLASS,
  INQUIRY_STATUS_LABEL,
} from '../constants.ts';

const isEmptyDoc = (doc?: JSONContent): boolean => {
  const content = doc?.content;
  return !content || content.length === 0;
};

/**
 * 문의 상세.
 * - 작성자 본인: 문의 내용 + (있으면) 답변 확인. 답변 전이면 수정/삭제.
 * - 운영자(MANAGER+): 답변 작성 폼 노출. 삭제 가능.
 * 열람 권한(본인/운영자)은 서버가 최종 판단(403 시 에러 표시).
 */
export function InquiryDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: inquiry, isLoading, isError, refetch } = useInquiryDetail(id);
  const deleteMutation = useDeleteInquiry();
  const answerMutation = useAnswerInquiry(id ?? '');
  const { mutate: markInquiryRead } =
    useMarkInquiryRead();
  const markedAnswerIdRef = useRef<string | number | null>(null);

  const [answer, setAnswer] = useState<JSONContent | undefined>();

  const isStaff = hasMinRole(user?.userRole ?? null, USER_ROLE.MANAGER);
  const isOwner = !!user?.id && !!inquiry?.authorId && user.id === inquiry.authorId;
  const isAdmin = hasMinRole(user?.userRole ?? null, USER_ROLE.ADMIN);

  useEffect(() => {
    if (
      !inquiry?.answerBody ||
      !isOwner ||
      markedAnswerIdRef.current === inquiry.id
    ) {
      return;
    }

    markedAnswerIdRef.current = inquiry.id;
    markInquiryRead(inquiry.id, {
      onError: () => {
        markedAnswerIdRef.current = null;
      },
    });
  }, [inquiry, isOwner, markInquiryRead]);

  const handleDelete = useCallback(async () => {
    if (!inquiry) return;
    const ok = await showConfirm({
      title: '문의 삭제',
      message: '이 문의를 삭제하시겠습니까? 되돌릴 수 없습니다.',
      tone: 'danger',
      confirmText: '삭제',
      cancelText: '취소',
      dismissible: false,
    });
    if (!ok) return;

    // 응답 값을 쓰지 않으므로 mutate + 콜백.
    // 실패 사유는 BE 문구를 그대로 노출한다("답변 완료된 문의는 삭제할 수 없습니다" 등).
    deleteMutation.mutate(inquiry.id, {
      onSuccess: () => navigate(PATHS.SUPPORT_INQUIRIES, { replace: true }),
      onError: (error) =>
        void showAlert({
          title: '삭제 실패',
          message: toUserMessage(error, '삭제 중 오류가 발생했습니다.'),
          tone: 'danger',
          confirmText: '확인',
        }),
    });
  }, [inquiry, deleteMutation, navigate]);

  const handleAnswer = useCallback(async () => {
    if (isEmptyDoc(answer)) {
      await showAlert({ title: '알림', message: '답변 내용을 입력해주세요.', confirmText: '확인' });
      return;
    }
    try {
      await answerMutation.mutateAsync({ answerBody: answer! });
      setAnswer(undefined);
    } catch (err) {
      await showAlert({
        title: '답변 실패',
        message: toUserMessage(err, '답변 등록 중 오류가 발생했습니다.'),
        confirmText: '확인',
      });
    }
  }, [answer, answerMutation]);

  if (isLoading) {
    return <StateBox>불러오는 중…</StateBox>;
  }
  if (isError || !inquiry) {
    return (
      <StateBox>
        문의를 불러오지 못했습니다.{' '}
        <button type="button" onClick={() => refetch()} className="font-semibold underline">
          다시 시도
        </button>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate(PATHS.SUPPORT_INQUIRIES)}
            className="text-[13px] font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-950 dark:hover:text-white"
          >
            ← 목록으로
          </button>
        </div>
      </StateBox>
    );
  }

  const canEdit = isOwner && inquiry.status === 'PENDING';
  const canDelete = isOwner || isAdmin;
  const seo = buildSeo({
    title: inquiry.title,
    description: inquiry.excerpt ?? '1:1 문의',
    path: PATHS.inquiryDetail(inquiry.id),
  });

  return (
    <>
      <SEO {...seo} />
      <main className="overflow-x-hidden bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="mx-auto max-w-3xl pt-[100px] sm:pt-28">
              <button
                type="button"
                onClick={() => navigate(PATHS.SUPPORT_INQUIRIES)}
                className="mb-4 text-[13px] font-semibold text-neutral-500 dark:text-neutral-400 transition-colors hover:text-neutral-950 dark:hover:text-white"
              >
                ← 목록으로
              </button>

              {/* 문의 헤더 */}
              <header className="border-b border-black/10 dark:border-white/10 pb-6">
                <div className="mb-3 flex items-center gap-2">
                  <span className="rounded-full bg-black/[0.04] dark:bg-white/10 px-2.5 py-0.5 text-[12px] font-semibold text-neutral-950/55 dark:text-white/55">
                    {INQUIRY_CATEGORY_LABEL[inquiry.category]}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[12px] font-bold ${INQUIRY_STATUS_CLASS[inquiry.status]}`}
                  >
                    {INQUIRY_STATUS_LABEL[inquiry.status]}
                  </span>
                </div>
                <h1 className="text-[clamp(24px,3.2vw,36px)] font-extrabold leading-tight -tracking-[0.03em]">
                  {inquiry.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-neutral-500 dark:text-neutral-400">
                  {inquiry.authorName && <span>{inquiry.authorName}</span>}
                  <span>{formatBoardDateTime(inquiry.createdAt)}</span>
                </div>

                {(canEdit || canDelete) && (
                  <div className="mt-5 flex gap-2">
                    {canEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(PATHS.inquiryEdit(inquiry.id))}
                      >
                        수정
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deleteMutation.isPending}
                        onClick={handleDelete}
                      >
                        삭제
                      </Button>
                    )}
                  </div>
                )}
              </header>

              {/* 문의 본문 */}
              <article className="py-8">
                {inquiry.body ? (
                  <PostViewer content={inquiry.body} />
                ) : (
                  <p className="text-neutral-400 dark:text-neutral-500">내용이 없습니다.</p>
                )}
              </article>

              {/* 답변 */}
              {inquiry.answerBody ? (
                <section className="rounded-2xl bg-black/[0.025] dark:bg-white/10 p-6">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-[14px] font-bold text-[#FA0F00]">
                      답변
                    </span>
                    <span className="text-[13px] text-neutral-500 dark:text-neutral-400">
                      {inquiry.answeredByName}
                      {inquiry.answeredAt &&
                        ` · ${formatBoardDateTime(inquiry.answeredAt)}`}
                    </span>
                  </div>
                  <PostViewer content={inquiry.answerBody} />
                </section>
              ) : (
                !isStaff && (
                  <section className="rounded-2xl border border-dashed border-black/10 dark:border-white/10 p-6 text-center text-[14px] text-neutral-500 dark:text-neutral-400">
                    아직 답변이 등록되지 않았습니다. 빠르게 확인 후 답변드리겠습니다.
                  </section>
                )
              )}

              {/* 운영자 답변 작성 */}
              {isStaff && (
                <section className="mt-8 border-t border-black/10 dark:border-white/10 pt-6">
                  <h2 className="mb-3 text-[15px] font-bold">
                    {inquiry.answerBody ? '답변 다시 작성' : '답변 작성'}
                  </h2>
                  <PostEditor onChange={setAnswer} />
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={handleAnswer}
                      loading={answerMutation.isPending}
                    >
                      답변 등록
                    </Button>
                  </div>
                </section>
              )}
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}

function StateBox({ children }: { children: React.ReactNode }) {
  return <main className="bg-white dark:bg-dark-base">
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-5 pt-28 text-center text-[14px] text-neutral-950/55 dark:text-white/55">
      {children}
    </div>
  </main>
}
